export interface TermDefinition {
  term: string;
  definition: string;
}

// Common terms used across multiple pages
const commonTerms: Record<string, TermDefinition> = {
  income: {
    term: "Income",
    definition: "Your total annual gross income before taxes and other deductions. This includes salary, wages, bonuses, and other regular income sources."
  },
  twoIncomes: {
    term: "Two Incomes", 
    definition: "Toggle this option if you're filing jointly with a spouse or partner and want to account for both incomes in the calculation."
  },
  taxYear: {
    term: "Tax Year",
    definition: "The calendar year for which tax calculations are being performed. Different years may have different tax brackets and rules."
  },
  state: {
    term: "State",
    definition: "Your state of residence for tax purposes. Different states have varying income tax rates, with some states having no income tax."
  },
  filingStatus: {
    term: "Filing Status",
    definition: "Your tax filing status (Single, Married Filing Jointly, Married Filing Separately, Head of Household). This affects your tax brackets and standard deductions."
  },
  currentAge: {
    term: "Current Age",
    definition: "Your current age in years. This is used to calculate how many years until retirement and contribution periods."
  },
  inflationRate: {
    term: "Inflation Rate",
    definition: "The expected annual rate at which prices for goods and services rise, typically expressed as a percentage. Used to adjust future values to today's purchasing power."
  },
  annualReturn: {
    term: "Annual Return",
    definition: "The expected average annual rate of return on your investments, expressed as a percentage. This is used to project future investment growth."
  },
  growContributions: {
    term: "Grow Contributions With Inflation",
    definition: "When enabled, your annual contributions will increase each year by the inflation rate to maintain their real purchasing power over time."
  }
};

// Terms specific to Income Tax Calculator
export const incomeTaxTerms: TermDefinition[] = [
  commonTerms.twoIncomes,
  commonTerms.income,
  commonTerms.taxYear,
  commonTerms.state,
  commonTerms.filingStatus,
  {
    term: "Medicare Tax",
    definition: "A federal payroll tax of 1.45% on all income, plus an additional 0.9% on income over $200,000 (single) or $250,000 (married filing jointly). Funds Medicare health insurance."
  },
  {
    term: "Social Security Tax",
    definition: "A federal payroll tax of 6.2% on income up to the annual wage base limit ($160,200 for 2023). Funds Social Security retirement, disability, and survivor benefits."
  },
  {
    term: "Federal Income Tax",
    definition: "Federal income tax calculated using progressive tax brackets. The rate increases as your income increases, with different brackets for different filing statuses."
  },
  {
    term: "State Income Tax",
    definition: "Income tax imposed by your state of residence. Rates vary significantly by state, with some states having no income tax and others having rates up to 13% or more."
  },
  {
    term: "Total Tax Owed",
    definition: "The sum of all federal and state taxes owed, including income tax, Medicare tax, and Social Security tax."
  }
];

// Terms specific to Net Worth Calculator
export const netWorthTerms: TermDefinition[] = [
  commonTerms.twoIncomes,
  {
    term: "Current Investments",
    definition: "The total current value of your existing investment accounts, including 401(k), IRA, brokerage accounts, and other investment vehicles."
  },
  {
    term: "Annual Savings",
    definition: "The total amount you plan to save and invest each year across all investment accounts."
  },
  commonTerms.currentAge,
  {
    term: "Stop Saving Age",
    definition: "The age at which you plan to stop making regular contributions to your investment accounts, typically at retirement."
  },
  commonTerms.growContributions,
  commonTerms.inflationRate,
  commonTerms.annualReturn
];

// Terms specific to Simplified Retirement Income Calculator
export const retirementIncomeTerms: TermDefinition[] = [
  commonTerms.twoIncomes,
  {
    term: "Current Investments",
    definition: "The total current value of your existing retirement investment accounts, including 401(k), IRA, and brokerage accounts."
  },
  {
    term: "Annual Savings",
    definition: "The total amount you plan to save and invest each year across all retirement accounts."
  },
  commonTerms.currentAge,
  {
    term: "Stop Saving Age",
    definition: "The age at which you plan to stop making regular contributions to your retirement accounts."
  },
  {
    term: "Retirement Age",
    definition: "The age at which you plan to start withdrawing from your retirement accounts to fund your retirement."
  },
  commonTerms.growContributions,
  {
    term: "Withdrawal Rate",
    definition: "The percentage of your total retirement savings you plan to withdraw each year during retirement. The '4% rule' is a common guideline."
  },
  commonTerms.annualReturn,
  commonTerms.inflationRate
];

// Terms specific to Takehome Pay Calculator
export const takehomePayTerms: TermDefinition[] = [
  commonTerms.twoIncomes,
  commonTerms.income,
  {
    term: "Charitable Giving",
    definition: "The percentage of your income that you donate to qualified charitable organizations. These donations may be tax-deductible and reduce your taxable income."
  },
  commonTerms.taxYear,
  commonTerms.state,
  commonTerms.filingStatus,
  {
    term: "Take-home Pay",
    definition: "Your net income after all taxes and charitable contributions are deducted from your gross income. This is the amount you actually receive."
  },
  {
    term: "Charity",
    definition: "The dollar amount of your charitable contributions based on your charitable giving percentage and income."
  },
  {
    term: "Federal Tax",
    definition: "The total federal taxes including income tax, Medicare tax, and Social Security tax."
  },
  {
    term: "Social Security Tax",
    definition: "A federal payroll tax of 6.2% on income up to the annual wage base limit. Funds Social Security benefits."
  },
  {
    term: "State Tax",
    definition: "Income tax imposed by your state of residence. Varies by state and filing status."
  },
  {
    term: "Medicare Tax",
    definition: "A federal payroll tax of 1.45% on all income, plus additional tax on high earners. Funds Medicare health insurance."
  }
];

// Terms specific to After Tax Retirement Income Optimizer
export const afterTaxRetirementTerms: TermDefinition[] = [
  commonTerms.twoIncomes,
  {
    term: "Trad Balance",
    definition: "Traditional Balance - The current value of your traditional (pre-tax) retirement accounts like traditional 401(k) and traditional IRA. Withdrawals are taxed as ordinary income."
  },
  {
    term: "Roth Balance", 
    definition: "The current value of your Roth retirement accounts like Roth 401(k) and Roth IRA. These are funded with after-tax dollars and qualified withdrawals are tax-free."
  },
  {
    term: "Brokerage Balance",
    definition: "The current value of your taxable investment accounts (non-retirement accounts). These have more flexibility but don't have tax advantages."
  },
  {
    term: "Yearly Trad 401k",
    definition: "Annual contributions to your traditional 401(k). These are pre-tax contributions that reduce your current taxable income."
  },
  {
    term: "Yearly Roth 401k",
    definition: "Annual contributions to your Roth 401(k). These are after-tax contributions that provide tax-free growth and withdrawals in retirement."
  },
  {
    term: "Yearly 401k Match",
    definition: "The annual amount your employer contributes to your 401(k) as a matching contribution. This is typically a percentage of your salary and contributions."
  },
  {
    term: "Yearly AfterTax 401k",
    definition: "Annual after-tax contributions to your 401(k) beyond the pre-tax and Roth limits. These can often be converted to Roth through in-service distributions."
  },
  {
    term: "Yearly Trad IRA",
    definition: "Annual contributions to your traditional IRA. These may be tax-deductible depending on your income and whether you have a workplace retirement plan."
  },
  {
    term: "Yearly Roth IRA", 
    definition: "Annual contributions to your Roth IRA. These are after-tax contributions with tax-free growth and qualified withdrawals."
  },
  {
    term: "Yearly Brokerage",
    definition: "Annual contributions to your taxable brokerage accounts. These offer flexibility but no special tax advantages."
  },
  commonTerms.income,
  commonTerms.currentAge,
  {
    term: "Stop Savings Age",
    definition: "The age at which you plan to stop making regular contributions to your retirement and investment accounts."
  },
  {
    term: "Retirement Age",
    definition: "The age at which you plan to start withdrawing from your retirement accounts. Early withdrawals may incur penalties."
  },
  commonTerms.growContributions,
  {
    term: "Withdrawal Rate",
    definition: "The percentage of your total retirement savings you plan to withdraw each year during retirement for living expenses."
  },
  {
    term: "Average Annual Return",
    definition: "The expected average annual rate of return on your investments across all account types, used for projecting future growth."
  },
  commonTerms.inflationRate,
  commonTerms.taxYear,
  commonTerms.state,
  commonTerms.filingStatus,
  {
    term: "Income Roth",
    definition: "The portion of your retirement income that comes from Roth accounts, which is generally tax-free."
  },
  {
    term: "Income Trad",
    definition: "The portion of your retirement income that comes from traditional (pre-tax) accounts, which is taxed as ordinary income."
  },
  {
    term: "Income Brokerage",
    definition: "The portion of your retirement income that comes from taxable brokerage accounts, which may be subject to capital gains tax."
  },
  {
    term: "Income Post Tax",
    definition: "Your after-tax retirement income after all applicable taxes have been deducted from your withdrawal amounts."
  }
];